import React, { useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPinIcon, MailIcon, PhoneIcon, InstagramIcon, FacebookIcon } from 'lucide-react';
interface LocationProps {
  onOpenBooking: () => void;
}
export function Location({
  onOpenBooking
}: LocationProps) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section id="location" className="w-full bg-cream py-24 md:py-32 px-6 lg:px-8" data-id="element-194">
      <div className="max-w-6xl mx-auto" ref={ref} data-id="element-195">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-196">
          {/* Info column */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7
        }} data-id="element-197">
            <p className="text-sm font-body font-medium tracking-[0.25em] uppercase text-sage mb-4" data-id="element-198">
              Visit Us
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-bark leading-tight mb-8" data-id="element-199">
              Find Your Way
              <br data-id="element-200" />
              to Us
            </h2>
            <p className="font-body text-bark-light text-base md:text-lg leading-relaxed mb-10" data-id="element-201">
              Just 30 minutes from Sibiu and a short drive from the medieval
              citadel of Sighișoara, Richiș is a hidden gem waiting to be
              discovered.
            </p>

            {/* Contact details */}
            <div className="space-y-5 mb-8" data-id="element-202">
              <div className="flex items-start gap-4" data-id="element-203">
                <div className="w-10 h-10 rounded-lg bg-stone flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-204">
                  <MapPinIcon className="w-5 h-5 text-terracotta" data-id="element-205" />
                </div>
                <div data-id="element-206">
                  <p className="font-body font-medium text-bark" data-id="element-207">Address</p>
                  <p className="font-body text-bark-light" data-id="element-208">
                    Strada Principală nr. 376, Richiș, Sibiu County, Romania
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-209">
                <div className="w-10 h-10 rounded-lg bg-stone flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-210">
                  <MailIcon className="w-5 h-5 text-terracotta" data-id="element-211" />
                </div>
                <div data-id="element-212">
                  <p className="font-body font-medium text-bark" data-id="element-213">Email</p>
                  <a href="mailto:hello@zenbarnspa.com" className="font-body text-terracotta hover:text-terracotta-dark transition-colors" data-id="element-214">
                    hello@zenbarnspa.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-215">
                <div className="w-10 h-10 rounded-lg bg-stone flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-216">
                  <PhoneIcon className="w-5 h-5 text-terracotta" data-id="element-217" />
                </div>
                <div data-id="element-218">
                  <p className="font-body font-medium text-bark" data-id="element-219">Phone</p>
                  <a href="tel:+40771047817" className="font-body text-terracotta hover:text-terracotta-dark transition-colors" data-id="element-220">
                    +40 771 047 817
                  </a>
                </div>
              </div>
            </div>

            {/* Social links */}
            <div className="flex items-center gap-3 mb-10" data-id="element-221">
              <a href="https://wa.me/40771047817" target="_blank" rel="noopener noreferrer" aria-label="Message us on WhatsApp" className="w-11 h-11 rounded-full bg-stone flex items-center justify-center hover:bg-terracotta/20 transition-colors duration-300" data-id="element-222">
                <svg className="w-5 h-5 text-bark-light" viewBox="0 0 24 24" fill="currentColor" data-id="element-223">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-224" />
                </svg>
              </a>
              <a href="https://www.instagram.com/zenbarnspa/" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram" className="w-11 h-11 rounded-full bg-stone flex items-center justify-center hover:bg-terracotta/20 transition-colors duration-300" data-id="element-225">
                <InstagramIcon className="w-5 h-5 text-bark-light" data-id="element-226" />
              </a>
              <a href="https://www.facebook.com/p/Zen-Barn-Spa-100064837326764/" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Facebook" className="w-11 h-11 rounded-full bg-stone flex items-center justify-center hover:bg-terracotta/20 transition-colors duration-300" data-id="element-227">
                <FacebookIcon className="w-5 h-5 text-bark-light" data-id="element-228" />
              </a>
            </div>

            <button onClick={onOpenBooking} className="inline-flex items-center px-8 py-4 bg-terracotta text-white font-body font-medium text-sm tracking-wide rounded-full hover:bg-terracotta-dark transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-cream" data-id="element-229">
              Book Your Retreat
            </button>
          </motion.div>

          {/* Google Maps embed */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.2
        }} data-id="element-230">
            <div className="relative aspect-square rounded-2xl overflow-hidden shadow-lg" data-id="element-231">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1500!2d24.4816346!3d46.0956434!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x474b87b38a979c77%3A0x137c29589a96c402!2sZen%20Barn%20Spa!5e0!3m2!1sen!2sro!4v1700000000000!5m2!1sen!2sro" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Zen Barn Spa location on Google Maps" className="absolute inset-0 w-full h-full" data-id="element-232" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}